
import { getCurrentConference } from '../base/conference';
import {
    PARTICIPANT_JOINED,
    PARTICIPANT_LEFT,
    PARTICIPANT_ROLE,
    PARTICIPANT_UPDATED,
    getParticipantById,
    getParticipantDisplayName
} from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { hasOtherSessionsOf } from '../session-persistence/functions';

import { SHOW_NOTIFICATION } from './actionTypes';
import {
    clearNotifications,
    showNotification,
    showParticipantJoinedNotification
} from './actions';
import { NOTIFICATION_TIMEOUT } from './constants';
import { joinLeaveNotificationsDisabled } from './functions';

declare var interfaceConfig: Object;

/**
 * Middleware that captures actions to display notifications.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    // While the conference is in its "closing all terminals" state
    // (CONFERENCE_WILL_LEAVE -> PreLoader on hangup) no further toast
    // notifications must pop up on top of the closing overlay. The
    // `conferenceEnded` flag on `features/base/conference` is set true at the
    // moment the user initiates hangup, and stays true until the next
    // conference is created. Any SHOW_NOTIFICATION action that fires while
    // that flag is true is silently dropped here.
    if (action.type === SHOW_NOTIFICATION) {
        const baseConference = store.getState()['features/base/conference'];

        if (baseConference?.conferenceEnded) {
            return null;
        }
    }

    switch (action.type) {
    case PARTICIPANT_JOINED: {
        const { participant: p } = action;
        const { dispatch, getState } = store;

        // Somebody whose tile is already on screen did not arrive - one of their
        // windows reconnected, or they opened another device. Announcing a join
        // for a person the room can already see is just noise.
        const isReconnect = hasOtherSessionsOf(getState(), p);
        const result = next(action);

        if (!p.local && !p?.isFakeParticipant && !isReconnect
                && !joinLeaveNotificationsDisabled(store.getState())) {
            dispatch(showParticipantJoinedNotification(
                getParticipantDisplayName(getState, p.id)
            ));
        }

        if (typeof interfaceConfig === 'object'
                && !interfaceConfig.DISABLE_FOCUS_INDICATOR && p.role === PARTICIPANT_ROLE.MODERATOR) {
            // Do not show the notification for mobile and also when the focus indicator is disabled.
            const displayName = getParticipantDisplayName(getState, p.id);

            dispatch(showNotification({
                descriptionArguments: { to: displayName || '$t(notify.somebody)' },
                descriptionKey: 'notify.grantedTo',
                titleKey: 'notify.somebody',
                title: displayName
            },
            NOTIFICATION_TIMEOUT));
        }


        return result;
    }
    case PARTICIPANT_LEFT: {
        if (!joinLeaveNotificationsDisabled(store.getState())) {
            const participant = getParticipantById(
                store.getState(),
                action.participant.id
            );

            // Only the last endpoint of a person leaving means the person left.
            // Leaves for a previous conference (e.g. breakout room switch) and
            // fake participants (shared video) are just local cleanup.
            if (typeof interfaceConfig === 'object'
                && participant
                && !participant.local
                && !participant.isFakeParticipant
                && action.participant.conference === getCurrentConference(store.getState())
                && !hasOtherSessionsOf(store.getState(), participant)) {
                store.dispatch(showNotification({
                    descriptionKey: 'notify.disconnected',
                    titleKey: 'notify.somebody',
                    title: participant.name
                }, NOTIFICATION_TIMEOUT));
            }
        }

        return next(action);
    }
    case PARTICIPANT_UPDATED: {
        if (typeof interfaceConfig === 'undefined' || interfaceConfig.DISABLE_FOCUS_INDICATOR) {
            // Do not show the notification for mobile and also when the focus indicator is disabled.
            return next(action);
        }

        const { id, role } = action.participant;
        const state = store.getState();
        const oldParticipant = getParticipantById(state, id);
        const oldRole = oldParticipant?.role;

        if (oldRole && oldRole !== role && role === PARTICIPANT_ROLE.MODERATOR) {
            const displayName = getParticipantDisplayName(state, id);

            store.dispatch(showNotification({
                descriptionArguments: { to: displayName || '$t(notify.somebody)' },
                descriptionKey: 'notify.grantedTo',
                titleKey: 'notify.somebody',
                title: displayName
            },
            NOTIFICATION_TIMEOUT));
        }

        return next(action);
    }
    }

    return next(action);
});

/**
 * StateListenerRegistry provides a reliable way to detect the leaving of a
 * conference, where we need to clean up the notifications.
 */
StateListenerRegistry.register(
    /* selector */ state => getCurrentConference(state),
    /* listener */ (conference, { dispatch }) => {
        if (!conference) {
            dispatch(clearNotifications());
        }
    }
);
